import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import './button.css'

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger'
export type ButtonSize = 'sm' | 'md' | 'lg'

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Zeigt einen Spinner und sperrt die Schaltflaeche. */
  loading?: boolean
  /** Fuellt die verfuegbare Breite. */
  block?: boolean
  iconLeft?: ReactNode
  iconRight?: ReactNode
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'secondary',
    size = 'md',
    loading = false,
    block = false,
    iconLeft,
    iconRight,
    disabled,
    children,
    className,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={['btn', className].filter(Boolean).join(' ')}
      data-variant={variant}
      data-size={size}
      data-block={block || undefined}
      data-loading={loading || undefined}
      disabled={disabled || loading}
      // Screenreader sollen erfahren, dass gearbeitet wird — nicht nur sehende.
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading && <span className="btn__spinner" aria-hidden="true" />}
      {!loading && iconLeft && <span className="btn__icon">{iconLeft}</span>}
      {children != null && <span className="btn__label">{children}</span>}
      {!loading && iconRight && <span className="btn__icon">{iconRight}</span>}
    </button>
  )
})

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant
  size?: ButtonSize
  /** Pflicht: eine Schaltflaeche ohne Text braucht einen zugaenglichen Namen. */
  label: string
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  function IconButton(
    { variant = 'ghost', size = 'md', label, children, className, type = 'button', ...rest },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        className={['btn', 'btn--icon', className].filter(Boolean).join(' ')}
        data-variant={variant}
        data-size={size}
        aria-label={label}
        title={label}
        {...rest}
      >
        {children}
      </button>
    )
  },
)
